import { useEffect, useMemo, useState } from 'react';
import { buildExampleFromSchema, getPrimaryContent, resolveSchema } from '../utils/openapi';

const SNIPPET_TABS = ['cURL', 'JavaScript', 'Python', 'Go'];
const FIXED_API_BASE_URL = 'https://api-waba.hawktrion.com';

export function buildUrl(baseUrl, path, pathParams, queryParams) {
  const interpolatedPath = pathParams.reduce((acc, entry) => {
    const token = `{${entry.name}}`;
    const value = entry.value ? encodeURIComponent(entry.value) : token;
    return acc.replaceAll(token, value);
  }, path);

  const queryPairs = queryParams
    .filter((entry) => entry.value !== '')
    .map((entry) => `${encodeURIComponent(entry.name)}=${encodeURIComponent(entry.value)}`)
    .join('&');

  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
  const normalizedPath = interpolatedPath.replace(/^\/+/, '');
  return `${normalizedBaseUrl}/${normalizedPath}${queryPairs ? `?${queryPairs}` : ''}`;
}

function buildSnippet(tab, { method, url, body, bearerToken }) {
  const bodyString = body ? JSON.stringify(body, null, 2) : null;
  const authLines = [];

  if (bearerToken) {
    authLines.push(`Authorization: Bearer ${bearerToken}`);
  }

  if (tab === 'cURL') {
    const headers = authLines.map((line) => `  --header '${line}'`).join(' \\\n');
    const bodyLine = bodyString ? ` \\\n  --header 'Content-Type: application/json' \\\n  --data '${JSON.stringify(body)}'` : '';
    return `curl --request ${method} \\\n  --url ${url}${headers ? ` \\\n${headers}` : ''}${bodyLine}`;
  }

  if (tab === 'JavaScript') {
    const headers = {};
    if (bearerToken) {
      headers.Authorization = `Bearer ${bearerToken}`;
    }
    if (bodyString) {
      headers['Content-Type'] = 'application/json';
    }

    return `const response = await fetch('${url}', {
  method: '${method}',
  headers: ${JSON.stringify(headers, null, 2)},
  ${bodyString ? `body: JSON.stringify(${bodyString})` : '// No body required'}
});

const data = await response.json();
console.log(response.status, data);`;
  }

  if (tab === 'Python') {
    return `import requests

url = '${url}'
headers = {
  ${bearerToken ? `'Authorization': 'Bearer ${bearerToken}',` : ''}
  ${bodyString ? "'Content-Type': 'application/json'" : ''}
}
payload = ${bodyString || 'None'}

response = requests.request('${method}', url, headers=headers, json=payload)
print(response.status_code)
print(response.json())`;
  }

  return `package main

import (
  "fmt"
  "io"
  "net/http"
  "strings"
)

func main() {
  body := strings.NewReader(${bodyString ? `\`${JSON.stringify(body)}\`` : '""'})
  req, _ := http.NewRequest("${method}", "${url}", body)
  ${bearerToken ? `req.Header.Set("Authorization", "Bearer ${bearerToken}")` : ''}
  ${bodyString ? 'req.Header.Set("Content-Type", "application/json")' : ''}

  client := &http.Client{}
  resp, _ := client.Do(req)
  defer resp.Body.Close()

  data, _ := io.ReadAll(resp.Body)
  fmt.Println(resp.StatusCode)
  fmt.Println(string(data))
}`;
}

function RequestPlayground({ spec, operation }) {
  const availableServers = useMemo(
    () => [
      {
        url: FIXED_API_BASE_URL,
        description: ''
      }
    ],
    []
  );

  const [selectedServer] = useState(FIXED_API_BASE_URL);
  const [bearerToken, setBearerToken] = useState('');
  const [activeSnippetTab, setActiveSnippetTab] = useState('cURL');
  const [pathValues, setPathValues] = useState({});
  const [queryValues, setQueryValues] = useState({});
  const [selectedExampleId, setSelectedExampleId] = useState('');
  const [requestBodyText, setRequestBodyText] = useState('{}');
  const [responseState, setResponseState] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const requestExamples = operation?.requestExamples || [];
  const selectedExample = useMemo(
    () => requestExamples.find((example) => example.id === selectedExampleId) || requestExamples[0] || null,
    [requestExamples, selectedExampleId]
  );

  useEffect(() => {
    if (!operation) {
      return;
    }

    const pathDefaults = {};
    const queryDefaults = {};

    operation.parameters
      .filter((param) => param.in === 'path')
      .forEach((param) => {
        pathDefaults[param.name] = param.schema?.default || '';
      });

    operation.parameters
      .filter((param) => param.in === 'query')
      .forEach((param) => {
        queryDefaults[param.name] = param.schema?.default ?? '';
      });

    setPathValues(pathDefaults);
    setQueryValues(queryDefaults);

    if (operation.requestExamples?.length) {
      setSelectedExampleId(operation.requestExamples[0].id);
      setRequestBodyText(operation.requestExamples[0].bodyText || '{}');
      setResponseState(null);
      return;
    }

    const requestSchema = resolveSchema(spec, getPrimaryContent(operation.requestBody?.content || {}).schema);
    const exampleBody = buildExampleFromSchema(spec, requestSchema);
    setRequestBodyText(JSON.stringify(exampleBody || {}, null, 2));
    setResponseState(null);
  }, [operation, spec]);

  useEffect(() => {
    if (!selectedExample) {
      return;
    }

    setRequestBodyText(selectedExample.bodyText || '{}');
    setResponseState(null);
  }, [selectedExample]);

  const pathParameters = operation?.parameters?.filter((param) => param.in === 'path') || [];
  const queryParameters = operation?.parameters?.filter((param) => param.in === 'query') || [];

  const requestBodyObject = useMemo(() => {
    try {
      return JSON.parse(requestBodyText);
    } catch (_error) {
      return null;
    }
  }, [requestBodyText]);

  const requestUrl = useMemo(() => {
    if (!operation || !selectedServer) {
      return '';
    }

    return buildUrl(
      selectedServer,
      operation.path,
      pathParameters.map((param) => ({ name: param.name, value: pathValues[param.name] || '' })),
      queryParameters.map((param) => ({ name: param.name, value: queryValues[param.name] || '' }))
    );
  }, [operation, selectedServer, pathParameters, pathValues, queryParameters, queryValues]);

  const snippet = useMemo(() => {
    if (!operation) {
      return '// Select an endpoint to generate code snippets.';
    }

    return buildSnippet(activeSnippetTab, {
      method: operation.method,
      url: requestUrl,
      body: requestBodyObject,
      bearerToken
    });
  }, [operation, activeSnippetTab, requestUrl, requestBodyObject, bearerToken]);

  const sendRequest = async () => {
    if (!operation) {
      return;
    }

    const headers = {};
    if (bearerToken) {
      headers.Authorization = `Bearer ${bearerToken}`;
    }

    let requestBody;
    if (operation.requestBody) {
      if (!requestBodyObject) {
        setResponseState({
          error: 'Request body must be valid JSON before sending.'
        });
        return;
      }
      headers['Content-Type'] = 'application/json';
      requestBody = JSON.stringify(requestBodyObject);
    }

    setIsSending(true);
    try {
      const response = await fetch(requestUrl, {
        method: operation.method,
        headers,
        body: requestBody
      });

      const rawText = await response.text();
      let parsedBody;
      try {
        parsedBody = JSON.parse(rawText);
      } catch (_error) {
        parsedBody = rawText;
      }

      setResponseState({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: parsedBody
      });
    } catch (error) {
      setResponseState({
        error: error.message || 'Request failed.'
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!operation) {
    return (
      <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-4 shadow-panel dark:border-slate-800 dark:bg-slate-950">
        <p className="text-sm text-slate-600 dark:text-slate-300">Select an endpoint to use the API playground.</p>
      </aside>
    );
  }

  return (
    <aside className="h-fit space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-panel dark:border-slate-800 dark:bg-slate-950">
      <section className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Environment</label>
        <select
          value={selectedServer}
          onChange={() => {}}
          disabled
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-cyan-400 transition focus:ring-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          {availableServers.map((server) => (
            <option key={server.url} value={server.url}>
              {server.url}
            </option>
          ))}
        </select>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Authentication</h3>
        <p className="text-xs text-slate-600 dark:text-slate-300">
          Get token URL:{' '}
          <a
            href="https://waba.hawktrion.com/integrations/token"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-cyan-600 hover:underline dark:text-cyan-400"
          >
            https://waba.hawktrion.com/integrations/token
          </a>
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-300">
          Login URL:{' '}
          <a
            href="https://waba.hawktrion.com/signin"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-cyan-600 hover:underline dark:text-cyan-400"
          >
            https://waba.hawktrion.com/signin
          </a>
        </p>
        <input
          value={bearerToken}
          onChange={(event) => setBearerToken(event.target.value)}
          placeholder="Bearer token"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-cyan-400 transition focus:ring-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        />
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">URL Preview</h3>
        <p className="break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-200">
          {requestUrl}
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Try It</h3>

        {pathParameters.length ? (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-slate-500">Path Parameters</p>
            {pathParameters.map((parameter) => (
              <input
                key={parameter.name}
                value={pathValues[parameter.name] || ''}
                onChange={(event) =>
                  setPathValues((previous) => ({
                    ...previous,
                    [parameter.name]: event.target.value
                  }))
                }
                placeholder={`${parameter.name}${parameter.required ? ' *' : ''}`}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-cyan-400 transition focus:ring-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            ))}
          </div>
        ) : null}

        {queryParameters.length ? (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-slate-500">Query Parameters</p>
            {queryParameters.map((parameter) => (
              <input
                key={parameter.name}
                value={queryValues[parameter.name] ?? ''}
                onChange={(event) =>
                  setQueryValues((previous) => ({
                    ...previous,
                    [parameter.name]: event.target.value
                  }))
                }
                placeholder={parameter.name}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-cyan-400 transition focus:ring-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            ))}
          </div>
        ) : null}

        {operation.requestBody ? (
          <div className="space-y-2">
            {requestExamples.length > 1 ? (
              <>
                <p className="text-[11px] font-semibold text-slate-500">Payload Example</p>
                <select
                  value={selectedExample?.id || ''}
                  onChange={(event) => setSelectedExampleId(event.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-cyan-400 transition focus:ring-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                >
                  {requestExamples.map((example) => (
                    <option key={example.id} value={example.id}>
                      {example.name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            <p className="text-[11px] font-semibold text-slate-500">Request Body (JSON)</p>
            <textarea
              value={requestBodyText}
              onChange={(event) => setRequestBodyText(event.target.value)}
              rows={10}
              className="w-full rounded-lg border border-slate-300 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100 outline-none ring-cyan-400 transition focus:ring-2 dark:border-slate-700"
            />
          </div>
        ) : null}

        <button
          type="button"
          onClick={sendRequest}
          disabled={isSending}
          className="w-full rounded-xl bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSending ? 'Sending...' : 'Send API Request'}
        </button>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Code Snippets</h3>
        <div className="flex flex-wrap gap-1">
          {SNIPPET_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveSnippetTab(tab)}
              className={`rounded-md px-2 py-1 text-xs font-semibold ${
                activeSnippetTab === tab
                  ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                  : 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <pre className="max-h-64 overflow-auto rounded-xl bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-100">
          {snippet}
        </pre>
      </section>

      {responseState ? (
        <section className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
          <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Response Viewer</h3>

          {responseState.error ? (
            <p className="rounded-lg bg-rose-50 px-2 py-2 text-xs text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
              {responseState.error}
            </p>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                HTTP {responseState.status} {responseState.statusText}
              </p>
              <details open>
                <summary className="cursor-pointer text-xs font-semibold text-slate-600 dark:text-slate-300">Headers</summary>
                <pre className="mt-2 max-h-32 overflow-auto rounded-lg bg-slate-100 p-2 font-mono text-[11px] dark:bg-slate-900">
                  {JSON.stringify(responseState.headers, null, 2)}
                </pre>
              </details>
              <details open>
                <summary className="cursor-pointer text-xs font-semibold text-slate-600 dark:text-slate-300">Body</summary>
                <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-slate-100 p-2 font-mono text-[11px] dark:bg-slate-900">
                  {typeof responseState.body === 'string'
                    ? responseState.body
                    : JSON.stringify(responseState.body, null, 2)}
                </pre>
              </details>
            </>
          )}
        </section>
      ) : null}
    </aside>
  );
}

export default RequestPlayground;
